import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { HELP_ARTICLES, getHelpArticle } from "@/data/help-articles";

export const Route = createFileRoute("/help/$slug")({
 loader: async ({ params }) => {
 await setSeoPageCache();
 const article = getHelpArticle(params.slug);
 if (!article) throw notFound();
 return { article };
 },
 head: ({ loaderData, params }) => {
 if (!loaderData) return { meta: [{ title: "Article not found, Heallexa" }] };
 const { article } = loaderData;
 const url = `https://heallexa.com/help/${params.slug}`;
 return {
 meta: [
 { title: `${article.title}, Heallexa` },
 { name: "description", content: article.description },
 { property: "og:title", content: article.title },
 { property: "og:description", content: article.description },
 { property: "og:url", content: url },
 { property: "og:type", content: "article" },
 ],
 links: [{ rel: "canonical", href: url }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "Article",
 headline: article.title,
 description: article.description,
 datePublished: article.updated,
 dateModified: article.updated,
 author: { "@type": "Organization", name: "Heallexa" },
 publisher: { "@type": "Organization", name: "Heallexa" },
 mainEntityOfPage: url,
 }),
 },
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "FAQPage",
 mainEntity: article.faqs.map((f) => ({
 "@type": "Question",
 name: f.q,
 acceptedAnswer: { "@type": "Answer", text: f.a },
 })),
 }),
 },
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Help Center", item: "https://heallexa.com/help" },
 { "@type": "ListItem", position: 2, name: article.title, item: url },
 ],
 }),
 },
 ],
 };
 },
 notFoundComponent: () => (
 <div className="min-h-screen bg-background">
 <Navbar />
 <div className="mx-auto max-w-3xl px-6 py-32 text-center">
 <h1 className="text-3xl font-semibold">Article not found</h1>
 <p className="mt-3 text-muted-foreground">This help article doesn't exist.</p>
 <Link to="/help" className="mt-6 inline-block text-primary hover:underline">← Back to Help Center</Link>
 </div>
 </div>
 ),
 errorComponent: ({ error }) => (
 <div className="min-h-screen bg-background">
 <Navbar />
 <div className="mx-auto max-w-3xl px-6 py-32 text-center">
 <h1 className="text-3xl font-semibold">Something went wrong</h1>
 <p className="mt-3 text-muted-foreground">{error.message}</p>
 </div>
 </div>
 ),
 component: HelpArticlePage,
});

function HelpArticlePage() {
 const { article } = Route.useLoaderData();
 const related = HELP_ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 3);

 return (
 <div className="min-h-screen bg-background pb-1">
 <Navbar />

 <header className="pt-28 pb-10 border-b border-border bg-card">
 <div className="mx-auto max-w-3xl px-6">
 <nav className="text-sm text-muted-foreground mb-4">
 <Link to="/help" className="hover:text-foreground">Help Center</Link>
 <span className="mx-2">/</span>
 <span className="text-foreground">{article.title}</span>
 </nav>
 <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground" style={{ letterSpacing: "-0.02em" }}>
 {article.title}
 </h1>
 <p className="mt-3 text-muted-foreground">{article.description}</p>
 <p className="mt-4 text-xs text-muted-foreground">
 Updated {new Date(article.updated).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
 </p>
 </div>
 </header>

 <main className="mx-auto max-w-3xl px-6 py-12">
 <article className="prose prose-neutral max-w-none">
 <p className="text-base text-foreground leading-relaxed">{article.intro}</p>

 {article.sections.map((s: any) => (
 <section key={s.h} className="mt-10">
 <h2 className="text-xl font-semibold text-foreground">{s.h}</h2>
 <p className="mt-3 text-foreground/90 leading-relaxed">{s.p}</p>
 </section>
 ))}

 <section className="mt-14">
 <h2 className="text-xl font-semibold text-foreground">Frequently asked questions</h2>
 <div className="mt-4 divide-y divide-border border border-border bg-card">
 {article.faqs.map((f: any) => (
 <details key={f.q} className="group p-5">
 <summary className="cursor-pointer font-medium text-foreground list-none flex justify-between items-center">
 {f.q}
 <span className="text-muted-foreground group-open:rotate-45 transition-transform">+</span>
 </summary>
 <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
 </details>
 ))}
 </div>
 </section>

 <section className="mt-14 border border-border bg-card p-6 text-center">
 <p className="font-medium text-foreground">Ready to find a doctor?</p>
 <p className="mt-1 text-sm text-muted-foreground">Search verified providers by specialty, location, and insurance.</p>
 <Link to="/browse" className="mt-4 inline-block bg-primary text-primary-foreground px-6 py-2.5 rounded hover:opacity-90 transition">
 Browse providers
 </Link>
 </section>

 {related.length > 0 && (
 <section className="mt-14">
 <h2 className="text-xl font-semibold text-foreground">Related guides</h2>
 <ul className="mt-4 space-y-3">
 {related.map((r) => (
 <li key={r.slug}>
 <Link to="/help/$slug" params={{ slug: r.slug }} className="text-primary hover:underline">
 {r.title}
 </Link>
 <p className="text-sm text-muted-foreground">{r.description}</p>
 </li>
 ))}
 </ul>
 </section>
 )}
 </article>
 </main>

 <FooterSection />
 </div>
 );
}
